import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { footerBus } from '@/lib/footer-bus'
import { isMotionPaused } from '@/lib/motion'
import { scramble } from '@/components/generals/scramble-text'

interface TitleBlockProps {
    sheet: number
    title: string
}

export const SHEET_TOTAL = 13

const SCRAMBLE_MS = 420
const BOTTOM_SLACK = 4
const LEAVE_SLACK = 160

const pad = (value: number): string => String(value).padStart(2, '0')
const sheetText = (sheet: number): string => `${pad(sheet)} of ${SHEET_TOTAL}`

const today = (): { date: string, rev: string } => {
    const now = new Date()

    return {
        date: `${now.getFullYear()}.${pad(now.getMonth() + 1)}.${pad(now.getDate())}`,
        rev: `${now.getFullYear()}.${pad(now.getMonth() + 1)}`
    }
}

/**
 * The drawing title block along the very bottom. SHEET shows the page's place in the index; when
 * the visitor reaches the very end of the page it scrambles to the final sheet, 13 OF 13, and the
 * skyline fires one scan pulse.
 */
export const TitleBlock: FC<TitleBlockProps> = ({ sheet, title }): ReactNode => {
    const sheetRef = useRef<HTMLSpanElement>(null)
    const [stamp, setStamp] = useState<{ date: string, rev: string } | null>(null)
    const [final, setFinal] = useState(false)

    useEffect(() => {
        const update = (): void => setStamp(today())

        update()

        const timer = window.setInterval(update, 30000)

        return () => window.clearInterval(timer)
    }, [])

    useEffect(() => {
        const element = sheetRef.current

        if (!element) return

        const reducedQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
        const pageText = sheetText(sheet)
        const finalText = sheetText(SHEET_TOTAL)

        let reached = false
        let raf = 0
        let scrollRaf = 0

        element.textContent = pageText

        const resolve = (text: string): void => {
            cancelAnimationFrame(raf)

            if (reducedQuery.matches || isMotionPaused()) {
                element.textContent = text

                return
            }

            const start = performance.now()

            const frame = (now: number): void => {
                const progress = Math.min(1, (now - start) / SCRAMBLE_MS)

                element.textContent = progress < 1 ? scramble(text, progress) : text

                if (progress < 1) raf = requestAnimationFrame(frame)
            }

            raf = requestAnimationFrame(frame)
        }

        const check = (): void => {
            const doc = document.documentElement
            const distance = doc.scrollHeight - (window.scrollY + window.innerHeight)
            const scrollable = doc.scrollHeight > window.innerHeight + 40

            if (!reached && scrollable && distance <= BOTTOM_SLACK) {
                reached = true
                setFinal(true)
                resolve(finalText)
                footerBus.emit('pulse')
            }
            else if (reached && distance > LEAVE_SLACK) {
                reached = false
                setFinal(false)
                cancelAnimationFrame(raf)
                element.textContent = pageText
            }
        }

        const onScroll = (): void => {
            cancelAnimationFrame(scrollRaf)
            scrollRaf = requestAnimationFrame(check)
        }

        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', onScroll)
        check()

        return () => {
            cancelAnimationFrame(raf)
            cancelAnimationFrame(scrollRaf)
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onScroll)
        }
    }, [sheet])

    const cells: Array<{ label: string, value: ReactNode }> = [
        { label: 'Project', value: 'Norrvik architects' },
        {
            label: 'Sheet',
            value: (
                <span key={sheet} ref={sheetRef} className='tabular-nums'>
                    {sheetText(sheet)}
                </span>
            )
        },
        { label: 'Title', value: title },
        { label: 'Scale', value: '1 : 200' },
        { label: 'Drawn', value: 'Studio' },
        { label: 'Date', value: <span className='tabular-nums' suppressHydrationWarning>{stamp?.date ?? '----.--.--'}</span> },
        { label: 'Rev', value: <span className='tabular-nums' suppressHydrationWarning>{stamp?.rev ?? '----.--'}</span> }
    ]

    return (
        <div>
            <dl className='grid grid-cols-2 border-t border-hairline desk:grid-cols-[1.5fr_1fr_1.4fr_0.9fr_0.9fr_1.1fr_0.9fr]'>
                {cells.map((cell, index) => (
                    <div
                        key={cell.label}
                        className={cn(
                            'min-w-0 border-hairline px-4 py-3 desk:px-5',
                            index % 2 === 1 && 'border-l',
                            index >= 2 && 'border-t',
                            index === cells.length - 1 && 'col-span-2 border-l-0 desk:col-span-1',
                            'desk:border-t-0',
                            index > 0 ? 'desk:border-l' : 'desk:border-l-0'
                        )}
                    >
                        <dt className='hud text-mute'>
                            {cell.label}
                        </dt>

                        <dd className='hud mt-1 truncate text-ink'>
                            {cell.value}
                        </dd>
                    </div>
                ))}
            </dl>

            <p className='sr-only' aria-live='polite'>
                {final ? `End of sheet, sheet ${SHEET_TOTAL} of ${SHEET_TOTAL}` : ''}
            </p>
        </div>
    )
}
